/**
 * Contextual coupon banner — renders the specific reason a coupon does or
 * doesn't apply (date window / min nights / property scope / etc.) instead
 * of a vague "doesn't apply" dead-end.
 *
 * Used on:
 *  - Search results page (above the property list)
 *  - Property detail page (above the room list)
 *  - BookingSidebar (inline under the promo input)
 *
 * Server- and client-component compatible: the eligibility decision is a
 * plain object, locale-aware date formatting happens here via Intl.
 */

import { Tag, AlertCircle } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { cn } from '@/lib/utils'
import { formatDayMonthYear } from '@/lib/date-display'
import type { CouponEligibility, CouponPreview } from '@/services/coupon'

interface CouponBannerProps {
  /** Outcome of evaluateCouponEligibility(preview, ctx). */
  eligibility: CouponEligibility
  /** When eligible, drives the "X% off" / "{amount} off" message. */
  preview?: CouponPreview
  /** Visual density — 'compact' is for the inline-under-input slot. */
  size?: 'default' | 'compact'
  className?: string
}

function fmtDate(iso: string, locale: string): string {
  // date-fns (NOT Intl): banner text renders in SSR HTML; Intl 'ar' output
  // differs between WebKit and Node → hydration mismatch on Safari.
  return formatDayMonthYear(iso, locale)
}

/** Pull the locale-appropriate property name from the row, with EN fallback. */
function propName(p: { name_en: string; name_ar: string | null }, locale: string): string {
  if (locale.startsWith('ar') && p.name_ar) return p.name_ar
  return p.name_en
}

function joinList(items: string[], locale: string): string {
  // Use Intl.ListFormat when available (Node 12+, all modern browsers).
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items)
  } catch {
    return items.join(', ')
  }
}

export function CouponBanner({ eligibility, preview, size = 'default', className }: CouponBannerProps) {
  const t = useTranslations('booking.search')
  const locale = useLocale()

  // Eligible: green "applied" banner using the existing keys.
  if (eligibility.eligible) {
    if (!preview || preview.discountValue == null) return null
    const message = preview.discountType === 'percentage'
      ? <><strong>{t('promoDiscountPercent', { percent: preview.discountValue })}</strong> &mdash; {t('appliedToBooking')}</>
      : <><strong>{t('promoDiscountFixed', { amount: preview.discountValue, currency: preview.currency || 'SAR' })}</strong> &mdash; {t('appliedAtCheckout')}</>
    return (
      <BannerShell tone="positive" size={size} className={className}>
        <Tag className={iconClass('positive')} />
        <span className={textClass('positive')}>{message}</span>
      </BannerShell>
    )
  }

  // Ineligible: pick the right key based on the reason discriminator.
  let message: React.ReactNode
  switch (eligibility.reason) {
    case 'expired':
      message = t('promoCodeExpired', { code: eligibility.code, date: fmtDate(eligibility.validUntil, locale) })
      break
    case 'not_yet':
      message = t('promoCodeNotYet', { code: eligibility.code, date: fmtDate(eligibility.validFrom, locale) })
      break
    case 'usage_exhausted':
      message = t('promoCodeUsageExhausted', { code: eligibility.code })
      break
    case 'stay_window': {
      const from = eligibility.stayFrom ? fmtDate(eligibility.stayFrom, locale) : null
      const until = eligibility.stayUntil ? fmtDate(eligibility.stayUntil, locale) : null
      if (from && until) {
        message = t('promoCodeStayWindow', { code: eligibility.code, from, until })
      } else if (until) {
        message = t('promoCodeStayWindowOpenStart', { code: eligibility.code, until })
      } else if (from) {
        message = t('promoCodeStayWindowOpenEnd', { code: eligibility.code, from })
      } else {
        message = t('promoCodeNotEligibleForSearch') + ` (${eligibility.code})`
      }
      break
    }
    case 'min_nights':
      message = t('promoCodeMinNights', { code: eligibility.code, count: eligibility.minNights })
      break
    case 'min_amount':
      // Keys for min_amount weren't seeded — fall back to the generic
      // "not eligible" message rather than printing a raw key. The amount
      // is also rarely the culprit for guest-typed codes.
      message = t('promoCodeNotEligibleForSearch') + ` (${eligibility.code})`
      break
    case 'property_scope': {
      if (eligibility.allowedProperties.length === 0) {
        message = t('promoCodeNotEligibleForSearch') + ` (${eligibility.code})`
      } else {
        const names = eligibility.allowedProperties.map(p => propName(p, locale))
        message = t('promoCodePropertyScope', { code: eligibility.code, properties: joinList(names, locale) })
      }
      break
    }
    case 'not_found':
      message = eligibility.code
        ? t('promoCodeInvalid') + ` (${eligibility.code})`
        : t('promoCodeInvalid')
      break
    case 'generic':
    default:
      message = eligibility.code
        ? t('promoCodeNotEligibleForSearch') + ` (${eligibility.code})`
        : t('promoCodeNotEligibleForSearch')
      break
  }

  return (
    <BannerShell tone="warning" size={size} className={className}>
      <AlertCircle className={iconClass('warning')} />
      <span className={textClass('warning')}>{message}</span>
    </BannerShell>
  )
}

// ============================================================================
// Internal styling helpers — kept inline so the banner can move between Server
// and Client component contexts without dragging in extra files.
// ============================================================================

type Tone = 'positive' | 'warning'

function BannerShell({
  tone, size, className, children,
}: {
  tone: Tone; size: 'default' | 'compact'; className?: string; children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'border rounded-lg flex items-center gap-2',
        size === 'compact' ? 'p-2 text-xs' : 'p-3 text-sm',
        tone === 'positive'
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
        className,
      )}
    >
      {children}
    </div>
  )
}

function iconClass(tone: Tone): string {
  return cn(
    'h-4 w-4 shrink-0',
    tone === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400',
  )
}

function textClass(tone: Tone): string {
  return tone === 'positive'
    ? 'text-green-700 dark:text-green-400'
    : 'text-amber-700 dark:text-amber-400'
}
